import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Message, Segment, Label, Form } from 'semantic';
import {
  EditEvseControllerEvseId,
  EditEvseControllerEvseIdActionType,
} from '../types';
import { useUser } from 'contexts/user';
import { request } from 'utils/api';

type StationIdFieldProps = {
  initialValues: any;
  formValues: any;
  locationId?: string;
  setEditEvseControllerEvseIdAction: (e?: EditEvseControllerEvseId) => void;
  setIsSetCustomIdAction: (a: boolean) => void;
};

export function StationIdField({
  initialValues,
  formValues,
  locationId,
  setEditEvseControllerEvseIdAction,
  setIsSetCustomIdAction,
}: StationIdFieldProps) {
  const { t } = useTranslation();
  const { provider } = useUser();

  const [countryCode, setCountryCode] = useState<string>('--');

  useEffect(() => {
    const getDefaultCountryCode = async (locationId?: string) => {
      if (!locationId) {
        return;
      }
      const { data } = await request<{ data: any }>({
        path: `/2/locations/cpo/${locationId}`,
        method: 'GET',
      });

      setCountryCode(data?.infraProviderId.slice(0, 2));
    };

    getDefaultCountryCode(locationId);
  }, [locationId]);

  let defaultPartyId = provider?.roamingParties[0]?.partyId;
  if (!provider?.enableCustomPartyIds || !defaultPartyId) {
    defaultPartyId = 'EFL';
  }

  const defaultEvseIdPrefix = `${countryCode}*${defaultPartyId}*E`;

  const actions = [
    {
      key: 'generateNewStationId',
      text: 'Generate New Station ID',
      value: 'generateNewStationId',
    },
    {
      key: 'enterStationIdFromSticker',
      text: 'Enter Station ID from Sticker',
      value: 'enterStationIdFromSticker',
    },
  ];

  if (provider?.enableCustomStationId) {
    actions.push({
      key: 'setCustomStationId',
      text: 'Set Custom Station ID',
      value: 'setCustomStationId',
    });
  }

  const [showActionSelect, setShowActionSelect] = useState(false);
  const [action, setAction] = useState<EditEvseControllerEvseIdActionType | ''>(
    ''
  );

  const showEvseIdStickerInput = action == 'enterStationIdFromSticker';
  const showCustomEvseIdInput = action == 'setCustomStationId';
  const showGenerateEvseIdWarning = action == 'generateNewStationId';
  const textInputDisabled = action == '' || action == 'generateNewStationId';

  const validateEvseId = (id: string) => {
    const pattern = /^[A-Za-z0-9*]{7,27}$/;
    return pattern.test(id);
  };

  const [customStationId, setCustomStationId] = useState('');

  return (
    <>
      <label style={{ fontSize: '13px' }}>
        <b>
          {t('editEvseControllers.stationIdFieldLabel', 'Station Identifier')}
        </b>
      </label>

      <Segment style={{ marginTop: '6px' }}>
        <Form.Input
          label={t('editEvseControllers.stationIdInputLabel', 'Station ID')}
          type="text"
          value={initialValues.evseId}
          readOnly>
          <input style={{ backgroundColor: 'rgba(220,220,220,0.25)' }} />
        </Form.Input>
        <Form.Checkbox
          toggle
          style={{ fontSize: '13px' }}
          label={t(
            'editEvseControllers.stationIdCheckboxLabel',
            'Update or Generate Station ID'
          )}
          onChange={() => {
            setShowActionSelect(!showActionSelect);
            setAction('');
            setEditEvseControllerEvseIdAction(undefined);
            setCustomStationId('');
          }}
        />

        {showActionSelect && (
          <>
            <Segment style={{ backgroundColor: 'rgba(220,220,220,0.25)' }}>
              <Form.Dropdown
                selection
                options={actions}
                placeholder={t('common.selectAction', 'Select Action')}
                label={t(
                  'editEvseControllers.stationIdActionLabel',
                  'Choose Action'
                )}
                onChange={async (e, { name, value }) => {
                  setAction(value as EditEvseControllerEvseIdActionType);
                  setIsSetCustomIdAction(value === 'setCustomStationId');
                  if (value == 'generateNewStationId') {
                    setEditEvseControllerEvseIdAction({
                      action: 'generateNewStationId',
                    });
                  }
                }}
              />

              {showGenerateEvseIdWarning && (
                <>
                  <Message warning>
                    <p
                      style={{
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: 'black',
                      }}>
                      {t(
                        'editEvseControllers.generateEvseIdWarningHeader',
                        'A new identifier will be generated and assigned to this charging station once this form is saved & updated.'
                      )}
                    </p>
                    <p
                      style={{
                        color: 'black',
                      }}>
                      {t(
                        'editEvseControllers.generateEvseIdWarningMessage',
                        `If there's an existing sticker on the station it will no longer be valid.`
                      )}
                    </p>
                  </Message>
                </>
              )}

              {showEvseIdStickerInput && (
                <>
                  <Form.Input
                    name="evseId"
                    label={t(
                      'editEvseControllers.stationIdInputLabel',
                      'Station ID'
                    )}
                    type="text"
                    disabled={textInputDisabled}
                    onChange={(e, { name, value }) =>
                      setEditEvseControllerEvseIdAction({
                        action: 'enterStationIdFromSticker',
                        stationId: value,
                      })
                    }
                  />
                  <Message warning>
                    <p
                      style={{
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: 'black',
                      }}>
                      {t(
                        'editEvseControllers.evseIdInputWarning',
                        'The specified identifier will be assigned to this charging station once this form is saved & updated.'
                      )}
                    </p>
                  </Message>
                </>
              )}

              {showCustomEvseIdInput && (
                <>
                  <Form.Input
                    name="evseId"
                    label={t(
                      'editEvseControllers.stationIdInputLabel',
                      'Station ID'
                    )}
                    type="text"
                    disabled={textInputDisabled}
                    labelPosition="left"
                    error={
                      customStationId !== ''
                        ? customStationId.startsWith('--')
                          ? {
                              content: t(
                                'editEvseControllers.selectLocationFirst',
                                'Please select a location first.'
                              ),
                              pointing: 'above',
                            }
                          : customStationId.length > 27
                            ? {
                                content: t(
                                  'editEvseControllers.stationIdTooLong',
                                  'Station ID is too long (maximum 27 characters).'
                                ),
                                pointing: 'above',
                              }
                            : customStationId.length < 7
                              ? {
                                  content: t(
                                    'editEvseControllers.stationIdTooShort',
                                    'Station ID is too short (minimum 7 characters).'
                                  ),
                                  pointing: 'above',
                                }
                              : !validateEvseId(customStationId)
                                ? {
                                    content: t(
                                      'editEvseControllers.invalidStationId',
                                      'Invalid Station ID. Use 7–27 characters and only letters (A–Z, a–z), digits (0–9), and asterisks (*).'
                                    ),
                                    pointing: 'above',
                                  }
                                : null
                        : null
                    }
                    onChange={(e, { value }) => {
                      const fullId = `${defaultEvseIdPrefix}${value}`;
                      setCustomStationId(fullId);
                      setEditEvseControllerEvseIdAction({
                        action: 'setCustomStationId',
                        stationId: fullId,
                      });
                    }}>
                    <Label basic>{defaultEvseIdPrefix}</Label>
                    <input />
                  </Form.Input>
                  <Message warning>
                    <p
                      style={{
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: 'black',
                      }}>
                      {t(
                        'editEvseControllers.evseIdInputWarning',
                        'The specified identifier will be assigned to this charging station once this form is saved & updated.'
                      )}
                    </p>
                  </Message>
                </>
              )}
            </Segment>
          </>
        )}
      </Segment>
    </>
  );
}
